import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Grid,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

const UserSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Search and filters
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(searchParams.get('subscription_status') || '');
  const [accountType, setAccountType] = useState(searchParams.get('account_type') || '');
  const [isSuperAdmin, setIsSuperAdmin] = useState(searchParams.get('is_super_admin') || '');
  const [emailVerified, setEmailVerified] = useState(searchParams.get('email_verified') || '');
  const [specialFilter, setSpecialFilter] = useState(searchParams.get('filter') || '');
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);

  // Create user dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    userType: 'caregiver', isComp: false, isSuperAdmin: false, sendWelcomeEmail: true,
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Load users when filters change
  useEffect(() => {
    // Read filters from URL on mount
    const q = searchParams.get('q');
    const subStatus = searchParams.get('subscription_status');
    const accType = searchParams.get('account_type');
    const superAdmin = searchParams.get('is_super_admin');
    const emailVer = searchParams.get('email_verified');
    const filter = searchParams.get('filter');
    
    if (q) setQuery(q);
    if (subStatus) setSubscriptionStatus(subStatus);
    if (accType) setAccountType(accType);
    if (superAdmin) setIsSuperAdmin(superAdmin);
    if (emailVer) setEmailVerified(emailVer);
    if (filter) setSpecialFilter(filter);
    
    // Show filters panel if any filter is active
    if (subStatus || accType || superAdmin || emailVer || filter) {
      setShowFilters(true);
    }
    
    loadUsers();
  }, [searchParams, page, rowsPerPage]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const q = searchParams.get('q');
      
      // If there's a search query, use search endpoint
      if (q && q.length >= 2) {
        const response = await adminAPI.searchUsers(q);
        setUsers(response.data.users);
        setTotal(response.data.total);
      } else {
        // Otherwise use list with filters
        const params = {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        };
        
        const subStatus = searchParams.get('subscription_status');
        const accType = searchParams.get('account_type');
        const superAdmin = searchParams.get('is_super_admin');
        const emailVer = searchParams.get('email_verified');
        const filter = searchParams.get('filter');
        
        if (subStatus) params.subscription_status = subStatus;
        if (accType) params.account_type = accType;
        if (superAdmin) params.is_super_admin = superAdmin;
        if (emailVer) params.email_verified = emailVer;
        if (filter) params.filter = filter;
        
        const response = await adminAPI.listUsers(params);
        setUsers(response.data.users);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ q: query.trim() || null });
  };

  const updateFilters = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setPage(0);
    setSearchParams(newParams);
  };

  const handleClearAll = () => {
    setQuery('');
    setSubscriptionStatus('');
    setAccountType('');
    setIsSuperAdmin('');
    setEmailVerified('');
    setSpecialFilter('');
    setSearchParams({});
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (user) => {
    // Managed clients don't have their own subscription — show "Managed" instead of "Inactive"
    if (user.managed_by_name) {
      return <Chip label="Managed" color="info" size="small" />;
    }
    if (!user.is_active) {
      return <Chip label="Inactive" color="default" size="small" />;
    }
    if (user.subscription_status === 'active') {
      return <Chip label="Active" color="success" size="small" />;
    }
    if (user.subscription_status === 'past_due') {
      return <Chip label="Past Due" color="warning" size="small" />;
    }
    if (user.subscription_status === 'canceled') {
      return <Chip label="Canceled" color="error" size="small" />;
    }
    return <Chip label={user.subscription_status || 'No Sub'} size="small" />;
  };

  const getUserTypeLabel = (user) => {
    if (user.managed_by_name) return 'Managed Client';
    if (user.account_type === 'owner' || user.role === 'caregiver') return 'Caregiver';
    if (user.account_type === 'client_solo' || user.role === 'client') return 'Client';
    if (user.role === 'both') return 'Both';
    return user.account_type || user.role || '-';
  };

  const getUserTypeColor = (user) => {
    if (user.managed_by_name) return 'info';
    if (user.account_type === 'owner' || user.role === 'caregiver') return 'secondary';
    if (user.account_type === 'client_solo' || user.role === 'client') return 'success';
    return 'default';
  };

  const hasActiveFilters = subscriptionStatus || accountType || isSuperAdmin || emailVerified || specialFilter || query;

  // --- Create User helpers ---

  const formatPhoneInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleCreateFormChange = (field, value) => {
    if (field === 'phone') {
      setCreateFormData(prev => ({ ...prev, phone: formatPhoneInput(value) }));
    } else {
      setCreateFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const resetCreateForm = () => {
    setCreateFormData({
      firstName: '', lastName: '', email: '', phone: '',
      userType: 'caregiver', isComp: false, isSuperAdmin: false, sendWelcomeEmail: true,
    });
    setFormError('');
  };

  const handleCreateUser = async () => {
    setFormError('');

    // Client-side validation
    const { firstName, lastName, email: formEmail, phone: formPhone } = createFormData;
    if (!firstName || firstName.trim().length < 2) {
      setFormError('First name must be at least 2 characters');
      return;
    }
    if (!lastName || lastName.trim().length < 2) {
      setFormError('Last name must be at least 2 characters');
      return;
    }
    if (!formEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())) {
      setFormError('Please enter a valid email address');
      return;
    }
    const phoneDigits = formPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setFormError('Phone number must be at least 10 digits');
      return;
    }

    try {
      setCreating(true);
      const payload = {
        ...createFormData,
        email: formEmail.trim(),
        phone: phoneDigits, // send raw digits, backend does E.164
      };
      const response = await adminAPI.createUser(payload);
      setCreateSuccess(response.data.message || 'User created successfully');
      setCreateDialogOpen(false);
      resetCreateForm();
      loadUsers();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // Get filter label for display
  const getFilterLabel = () => {
    const labels = [];
    if (specialFilter === 'new_7d') labels.push('New (7 days)');
    if (specialFilter === 'pending_verifications') labels.push('Pending Verifications');
    if (specialFilter === 'recent_activity') labels.push('Recent Activity');
    if (subscriptionStatus) labels.push(`Sub: ${subscriptionStatus}`);
    if (accountType) labels.push(`Type: ${accountType}`);
    if (isSuperAdmin === 'true') labels.push('Super Admins');
    if (emailVerified === 'false') labels.push('Unverified Email');
    return labels.join(', ');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>Users</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateDialogOpen(true)}>
          Add User
        </Button>
      </Box>

      {createSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateSuccess('')}>
          {createSuccess}
        </Alert>
      )}

      {/* Active filters display */}
      {hasActiveFilters && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          }
        >
          Filters: {getFilterLabel() || query}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            fullWidth
            placeholder="Search by email, phone, name, or Stripe ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {query && (
                    <IconButton onClick={() => { setQuery(''); updateFilters({ q: null }); }} edge="end" size="small">
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => setShowFilters(!showFilters)} edge="end" color={showFilters ? 'primary' : 'default'}>
                    <FilterIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Subscription"
                value={subscriptionStatus}
                onChange={(e) => { setSubscriptionStatus(e.target.value); updateFilters({ subscription_status: e.target.value || null }); }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="past_due">Past Due</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Account Type"
                value={accountType}
                onChange={(e) => { setAccountType(e.target.value); updateFilters({ account_type: e.target.value || null }); }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
                <MenuItem value="client_solo">Client Solo</MenuItem>
                <MenuItem value="client">Client</MenuItem>
                <MenuItem value="invited">Invited</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Super Admin"
                value={isSuperAdmin}
                onChange={(e) => { setIsSuperAdmin(e.target.value); updateFilters({ is_super_admin: e.target.value || null }); }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Email Verified"
                value={emailVerified}
                onChange={(e) => { setEmailVerified(e.target.value); updateFilters({ email_verified: e.target.value || null }); }}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Verified</MenuItem>
                <MenuItem value="false">Unverified</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Qty</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {user.name || '-'}
                          </Typography>
                          {user.is_super_admin && (
                            <Chip
                              icon={<AdminIcon sx={{ fontSize: 14 }} />}
                              label="Admin"
                              size="small"
                              color="secondary"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 11 } }}
                            />
                          )}
                          {parseInt(user.linked_clients_count) > 0 && (
                            <Chip
                              icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                              label={user.linked_clients_count}
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 11 } }}
                            />
                          )}
                        </Box>
                        {user.managed_by_name && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            Managed by:{' '}
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary"
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={(e) => { e.stopPropagation(); navigate(`/users/${user.managed_by_id}`); }}
                            >
                              {user.managed_by_name}
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {user.phone ? `***-***-${user.phone.slice(-4)}` : '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        label={getUserTypeLabel(user)}
                        size="small"
                        color={getUserTypeColor(user)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(user)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{user.subscription_quantity || 0}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {user.created_at
                        ? format(new Date(user.created_at), 'MMM d, yyyy')
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => { setCreateDialogOpen(false); resetCreateForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={createFormData.firstName}
                onChange={(e) => handleCreateFormChange('firstName', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                fullWidth
                required
                value={createFormData.lastName}
                onChange={(e) => handleCreateFormChange('lastName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={createFormData.email}
                onChange={(e) => handleCreateFormChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone"
                fullWidth
                required
                placeholder="(555) 123-4567"
                value={createFormData.phone}
                onChange={(e) => handleCreateFormChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={createFormData.userType}
                  label="User Type"
                  onChange={(e) => handleCreateFormChange('userType', e.target.value)}
                >
                  <MenuItem value="caregiver">Caregiver</MenuItem>
                  <MenuItem value="subscriber">Subscriber</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createFormData.isComp}
                    onChange={(e) => handleCreateFormChange('isComp', e.target.checked)}
                  />
                }
                label="Comp Account — Skip billing, grant immediate access"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createFormData.isSuperAdmin}
                    onChange={(e) => handleCreateFormChange('isSuperAdmin', e.target.checked)}
                  />
                }
                label="Make Admin"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createFormData.sendWelcomeEmail}
                    onChange={(e) => handleCreateFormChange('sendWelcomeEmail', e.target.checked)}
                  />
                }
                label="Send welcome email with password setup link"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); resetCreateForm(); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <PersonAddIcon />}
          >
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserSearch;
